import { useCallback, useEffect, useRef, useState } from 'react';
import type { CsvImportJob, KnowledgeDocument, KnowledgeSearchHit, KnowledgeSource } from '@supporchat/shared';
import { api } from '../lib/api';

interface Props {
  canWrite: boolean;
  importEvent: CsvImportJob | null;
}

type CsvPreview = Awaited<ReturnType<typeof api.previewCsv>>;

const SOURCE_LABELS: Record<KnowledgeSource, string> = {
  csv: 'Importado',
  conversation: 'Aprendido de conversa',
  manual: 'Cadastrado',
};

export function KnowledgePage({ canWrite, importEvent }: Props) {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof api.knowledgeStats>> | null>(null);
  const [sourceFilter, setSourceFilter] = useState<KnowledgeSource | ''>('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, statistics] = await Promise.all([
        api.listKnowledge({ source: sourceFilter || undefined, search: search || undefined }),
        api.knowledgeStats(),
      ]);
      setDocuments(list.items);
      setStats(statistics);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [sourceFilter, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  // A indexação termina depois da resposta HTTP, então recarregamos quando um
  // import avança para refletir o estado real.
  useEffect(() => {
    if (importEvent?.status === 'completed') void load();
  }, [importEvent, load]);

  return (
    <div className="page">
      <div className="page__inner">
        {error && <div className="alert alert--error">{error}</div>}

        {stats && (
          <div className="card">
            <div className="stat-row">
              <div className="stat">
                <div className="stat__value">{stats.total}</div>
                <div className="stat__label">itens na base</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.ready}</div>
                <div className="stat__label">indexados</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.pending}</div>
                <div className="stat__label">na fila</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.failed}</div>
                <div className="stat__label">com falha</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.bySource.conversation ?? 0}</div>
                <div className="stat__label">vindos de conversas</div>
              </div>
            </div>
          </div>
        )}

        <SearchTester />

        {canWrite && <CsvImportCard importEvent={importEvent} onImported={() => void load()} />}
        {canWrite && <NewDocumentCard onCreated={() => void load()} />}

        <div className="card">
          <h3 className="card__title">Itens da base</h3>
          <p className="card__subtitle">
            Itens desativados não são usados pelo agente. Conhecimento aprendido de conversas com confiança
            média entra desativado, para revisão.
          </p>

          <div className="btn-row" style={{ marginBottom: 12 }}>
            <input
              type="search"
              placeholder="Filtrar por texto"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ padding: '6px 9px', flex: 1, minWidth: 180 }}
            />
            <select
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value as KnowledgeSource | '')}
              style={{ padding: '6px 9px' }}
            >
              <option value="">Todas as origens</option>
              <option value="manual">Cadastrados</option>
              <option value="csv">Importados</option>
              <option value="conversation">Aprendidos de conversas</option>
            </select>
          </div>

          {documents.length === 0 ? (
            <div className="empty">Nenhum item encontrado.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Origem</th>
                  <th>Categoria</th>
                  <th>Status</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr key={document.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{document.title}</div>
                      {document.answer && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {document.answer.slice(0, 140)}
                          {document.answer.length > 140 && '...'}
                        </div>
                      )}
                    </td>
                    <td className="muted">{SOURCE_LABELS[document.source]}</td>
                    <td className="muted">{document.category ?? '—'}</td>
                    <td>
                      <EmbeddingBadge document={document} />
                    </td>
                    {canWrite && (
                      <td>
                        <div className="btn-row">
                          <button
                            className="btn btn--sm"
                            onClick={async () => {
                              await api.setKnowledgeActive(document.id, !document.active);
                              void load();
                            }}
                          >
                            {document.active ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            className="btn btn--sm btn--danger"
                            onClick={async () => {
                              await api.deleteKnowledge(document.id);
                              void load();
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EmbeddingBadge({ document }: { document: KnowledgeDocument }) {
  if (!document.active) return <span className="badge badge--closed">Desativado</span>;
  if (document.embeddingStatus === 'ready') return <span className="badge badge--ready">Ativo</span>;
  if (document.embeddingStatus === 'failed') return <span className="badge badge--failed">Falha</span>;
  return <span className="badge badge--pending">Indexando</span>;
}

/** Roda a mesma busca híbrida do agente: valida a base antes de ligá-lo. */
function SearchTester() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<KnowledgeSearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      setHits(await api.searchKnowledge(query.trim()));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h3 className="card__title">Testar a busca</h3>
      <p className="card__subtitle">
        Escreva como um cliente escreveria. O resultado é exatamente o que o agente receberia como contexto.
      </p>

      <div className="btn-row">
        <input
          placeholder="Ex.: qual o prazo de entrega para o interior?"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && void run()}
          style={{ flex: 1, padding: '7px 9px', minWidth: 220 }}
        />
        <button className="btn btn--primary" disabled={loading || !query.trim()} onClick={() => void run()}>
          {loading ? 'Buscando' : 'Buscar'}
        </button>
      </div>

      {hits && hits.length === 0 && (
        <div className="alert alert--info" style={{ marginTop: 12 }}>
          Nada encontrado. Com a base assim, o agente transferiria essa conversa para um atendente.
        </div>
      )}

      {hits && hits.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {hits.map((hit) => (
            <div key={hit.chunkId} className="hit">
              <div className="hit__head">
                <strong>{hit.title}</strong>
                {hit.category && <span className="badge badge--closed">{hit.category}</span>}
                <span className="hit__score">
                  rrf {hit.score.toFixed(4)}
                  {hit.vectorScore !== null && ` · vetor ${hit.vectorScore.toFixed(3)}`}
                  {hit.textScore !== null && ` · texto ${hit.textScore.toFixed(3)}`}
                </span>
              </div>
              <div className="hit__content">{hit.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CsvImportCard({
  importEvent,
  onImported,
}: {
  importEvent: CsvImportJob | null;
  onImported: () => void;
}) {
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<CsvImportJob | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (importEvent && (!job || importEvent.id === job.id)) {
      setJob(importEvent);
      if (importEvent.status === 'completed') onImported();
    }
  }, [importEvent, job, onImported]);

  const choose = async (selected: File) => {
    setError(null);
    setFile(selected);
    setBusy(true);
    try {
      setPreview(await api.previewCsv(selected));
    } catch (err) {
      setError((err as Error).message);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setJob(await api.importCsv(file));
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const progress = job && job.totalRows > 0 ? ((job.importedRows + job.failedRows) / job.totalRows) * 100 : 0;

  return (
    <div className="card">
      <h3 className="card__title">Importar base de um arquivo CSV</h3>
      <p className="card__subtitle">
        Duas formas de organizar a planilha: <strong>pergunta;resposta</strong> para FAQ ou{' '}
        <strong>titulo;conteudo</strong> para textos maiores. Colunas opcionais:{' '}
        <span className="mono">categoria</span>, <span className="mono">tags</span>,{' '}
        <span className="mono">id_externo</span>. Separador, acentos no cabeçalho e colunas extras são
        detectados automaticamente.
      </p>

      {error && <div className="alert alert--error">{error}</div>}

      <div
        className={`dropzone${dragging ? ' dropzone--active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) void choose(dropped);
        }}
        onClick={() => inputRef.current?.click()}
      >
        {file ? `${file.name} (${(file.size / 1024).toFixed(0)} KB)` : 'Arraste o CSV aqui ou clique para escolher'}
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(event) => {
            const selected = event.target.files?.[0];
            if (selected) void choose(selected);
          }}
        />
      </div>

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div className={preview.valid > 0 ? 'alert alert--success' : 'alert alert--error'}>
            {preview.valid} linha(s) prontas para importar
            {preview.errors.length > 0 && ` · ${preview.errors.length} com problema`}
            {` · separador detectado: "${preview.delimiter === '\t' ? 'tab' : preview.delimiter}"`}
          </div>

          {preview.unknownColumns.length > 0 && (
            <div className="alert alert--info">
              Colunas ignoradas: {preview.unknownColumns.join(', ')}
            </div>
          )}

          {preview.errors.length > 0 && (
            <table className="table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Linha</th>
                  <th>Problema</th>
                </tr>
              </thead>
              <tbody>
                {preview.errors.map((item, index) => (
                  <tr key={`${item.line}-${index}`}>
                    <td className="mono">{item.line}</td>
                    <td>{item.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {preview.sample.length > 0 && (
            <table className="table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Prévia — título</th>
                  <th>Categoria</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((item, index) => (
                  <tr key={index}>
                    <td>{item.title}</td>
                    <td className="muted">{item.category ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="btn-row">
            <button className="btn btn--primary" disabled={busy || preview.valid === 0} onClick={() => void confirm()}>
              Importar {preview.valid} item(ns)
            </button>
            <button
              className="btn"
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {job && (
        <div style={{ marginTop: 14 }}>
          <div className="btn-row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <span className="muted">
              {job.filename} · {translateImportStatus(job.status)}
            </span>
            <span className="mono">
              {job.importedRows}/{job.totalRows}
              {job.failedRows > 0 && ` · ${job.failedRows} com erro`}
            </span>
          </div>
          <div className="progress">
            <div className="progress__bar" style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          {job.status === 'completed' && (
            <p className="field__hint" style={{ marginTop: 6 }}>
              A geração dos embeddings continua em segundo plano; os itens ficam ativos conforme são
              indexados.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function NewDocumentCard({ onCreated }: { onCreated: () => void }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!question.trim() || !answer.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createKnowledge({
        title: question.trim().slice(0, 200),
        question: question.trim(),
        answer: answer.trim(),
        category: category.trim() || undefined,
      });
      setQuestion('');
      setAnswer('');
      setCategory('');
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3 className="card__title">Adicionar item manualmente</h3>
      <p className="card__subtitle">Escreva a pergunta como o cliente faria — é assim que a busca acerta.</p>

      {error && <div className="alert alert--error">{error}</div>}

      <div className="field">
        <label htmlFor="kb-question">Pergunta</label>
        <input
          id="kb-question"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Vocês entregam no sábado?"
        />
      </div>

      <div className="field">
        <label htmlFor="kb-answer">Resposta</label>
        <textarea
          id="kb-answer"
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          placeholder="Entregamos de segunda a sábado, das 8h às 18h."
        />
      </div>

      <div className="field">
        <label htmlFor="kb-category">Categoria (opcional)</label>
        <input
          id="kb-category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          placeholder="entrega"
        />
      </div>

      <button className="btn btn--primary" disabled={busy || !question.trim() || !answer.trim()} onClick={() => void submit()}>
        {busy ? 'Salvando' : 'Salvar item'}
      </button>
    </div>
  );
}

function translateImportStatus(status: CsvImportJob['status']): string {
  const labels: Record<CsvImportJob['status'], string> = {
    pending: 'na fila',
    processing: 'importando',
    completed: 'concluída',
    failed: 'falhou',
  };
  return labels[status];
}
